import {Mark, mergeAttributes, Node} from '@tiptap/core'
import {Extension, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer} from '@tiptap/react'
import Heading from '@tiptap/extension-heading'
import Paragraph from '@tiptap/extension-paragraph'
import Document from '@tiptap/extension-document'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Link from '@tiptap/extension-link'
import HardBreak from '@tiptap/extension-hard-break'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'
import History from '@tiptap/extension-history'
import {useMemo} from 'react'
import {CharacterCount} from '@tiptap/extension-character-count'
import Placeholder from '@tiptap/extension-placeholder'

import {H3, Ol, P, Span, Ul} from '@sdox/ui'
import {IS_TEST} from '@sdox/utils/dom'

import type {EditorConfig} from '../'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    footnote: {
      addFootnote: (identifier: string) => ReturnType
      updateFootnote: (identifier: string) => ReturnType
      removeFootnote: () => ReturnType
    }
    nobreak: {
      setNoBreak: () => ReturnType
      toggleNoBreak: () => ReturnType
      unsetNoBreak: () => ReturnType
    }
    small: {
      setSmall: () => ReturnType
      toggleSmall: () => ReturnType
      unsetSmall: () => ReturnType
    }
    medium: {
      setMedium: () => ReturnType
      toggleMedium: () => ReturnType
      unsetMedium: () => ReturnType
    }
    finePrint: {
      setFinePrint: () => ReturnType
      toggleFinePrint: () => ReturnType
      unsetFinePrint: () => ReturnType
    }
  }
}

const HeadingExt = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Enter': () => this.editor.commands.createParagraphNear(),
    }
  },
  parseHTML: () => [{tag: 'h1'}, {tag: 'h2'}, {tag: 'h3'}, {tag: 'h4'}],
  renderHTML: ({node}) => [node?.attrs?.withoutHTMLTag ? '' : 'h3', {}, 0],
  addNodeView: () => ReactNodeViewRenderer(
    ({HTMLAttributes}) => (
      <NodeViewWrapper>
        <H3 {...HTMLAttributes}>
          <NodeViewContent />
        </H3>
      </NodeViewWrapper>
    ),
  ),
})

const ParagraphExt = Paragraph.extend({
  addKeyboardShortcuts() {
    return {
      'Enter': () => this.editor.commands.createParagraphNear(),
    }
  },
  parseHTML: () => [{tag: 'h5'}, {tag: 'p'}],
  addNodeView: () => ReactNodeViewRenderer(
    ({HTMLAttributes}) => (
      <NodeViewWrapper>
        <P {...HTMLAttributes}>
          <NodeViewContent />
        </P>
      </NodeViewWrapper>
    ),
  ),
})

/**
 * Use this instead of ParagraphExt
 * Uses <span data-paragraph="true"> instead of <p> as default element.
 */
const ParagraphSpanExt = Paragraph.extend({
  addKeyboardShortcuts() {
    return {
      'Enter': () => this.editor.commands.createParagraphNear(),
    }
  },
  parseHTML: () => [{tag: 'span[data-paragraph]'}],
  renderHTML() {
    return ['span', {'data-paragraph': true}, 0]
  },
})

const SmallExt = Mark.create({
  name: 'small',
  addCommands() {
    return {
      setSmall: () => ({commands}) => {
        commands.unsetMark('medium')
        return commands.setMark(this.name)
      },
      toggleSmall: () => ({commands, editor}) => {
        if (!editor.isActive('small')) {
          commands.unsetMark('medium')
        }
        return commands.toggleMark(this.name)
      },
      unsetSmall: () => ({commands}) => {
        return commands.unsetMark(this.name)
      },
    }
  },
  parseHTML: () => [{tag: 'small'}],
  renderHTML: () => ['small', IS_TEST ? {'data-testid': '_small'} : {}, 0],
})

const BoldExt = Bold.extend({
  renderHTML: () => ['b', IS_TEST ? {'data-testid': '_strong'} : {}, 0],
})

const ItalicExt = Italic.extend({
  renderHTML: () => ['i', IS_TEST ? {'data-testid': '_em'} : {}, 0],
})

const NoBreak = Mark.create({
  name: 'nobreak',
  addKeyboardShortcuts() {
    return {
      'Mod-j': () => this.editor.commands.toggleNoBreak(),
    }
  },
  addCommands() {
    return {
      setNoBreak: () => ({commands}) => {
        return commands.setMark(this.name)
      },
      toggleNoBreak: () => ({commands}) => {
        return commands.toggleMark(this.name)
      },
      unsetNoBreak: () => ({commands}) => {
        return commands.unsetMark(this.name)
      },
    }
  },
  parseHTML: () => [{tag: 'span[data-no-break]'}],
  renderHTML: () => ['span', {
    'data-testid': IS_TEST ? '_nobreak' : null,
    'data-no-break': true,
  }, 0],
})

const Medium = Mark.create({
  name: 'medium',
  addCommands() {
    return {
      setMedium: () => ({commands}) => {
        commands.unsetMark('small')
        return commands.setMark(this.name)
      },
      toggleMedium: () => ({commands, editor}) => {
        if (!editor.isActive('medium')) {
          commands.unsetMark('small')
        }
        return commands.toggleMark(this.name)
      },
      unsetMedium: () => ({commands}) => {
        return commands.unsetMark(this.name)
      },
    }
  },
  parseHTML: () => [{tag: 'span[data-text-medium]'}],
  renderHTML: () => ['span', {
    'data-testid': IS_TEST ? '_medium' : null,
    'data-text-medium': true,
  }, 0],
})

const FinePrintExt = Mark.create({
  name: 'finePrint',
  addCommands() {
    return {
      setFinePrint: () => ({commands}) => {
        return commands.setMark(this.name)
      },
      toggleFinePrint: () => ({commands}) => {
        return commands.toggleMark(this.name)
      },
      unsetFinePrint: () => ({commands}) => {
        return commands.unsetMark(this.name)
      },
    }
  },
  parseHTML: () => [{tag: 'dfn'}],
  renderHTML: () => ['dfn', {
    'data-testid': IS_TEST ? '_dfn' : null,
  }, 0],
})

const OrderedListExt = OrderedList.extend({
  renderHTML: () => ['ol', {}, 0],
  addNodeView: () => ReactNodeViewRenderer(
    ({HTMLAttributes}) => (
      <NodeViewWrapper>
        <Ol {...HTMLAttributes}>
          <NodeViewContent />
        </Ol>
      </NodeViewWrapper>
    ),
  ),
})

const BulletListExt = BulletList.extend({
  renderHTML: () => ['ul', {}, 0],
  addNodeView: () => ReactNodeViewRenderer(
    ({HTMLAttributes}) => (
      <NodeViewWrapper>
        <Ul {...HTMLAttributes}>
          <NodeViewContent />
        </Ul>
      </NodeViewWrapper>
    ),
  ),
})

const FootnoteExt = Node.create({
  name: 'footnote',
  group: 'inline',
  marks: '',
  selectable: true,
  inline: true,
  parseHTML: () => [{tag: 'span[data-footnote]'}],
  addOptions: () => ({
    HTMLAttributes: {},
  }),
  addAttributes: () => ({
    'data-footnote': {
      default: null,
    },
  }),
  addCommands: () => ({
    addFootnote: (identifier) => ({chain}) => {
      return chain()
        .insertContent(`<span data-footnote="${identifier}" />`)
        .run()
    },
    updateFootnote: (identifier) => ({chain}) => {
      return chain()
        .updateAttributes('footnote', {'data-footnote': identifier})
        .run()
    },
    removeFootnote: () => ({chain}) => {
      return chain()
        .deleteNode('footnote')
        .run()
    },
  }),
  renderHTML: ({HTMLAttributes}) => [
    'span',
    mergeAttributes(HTMLAttributes),
  ],
  addNodeView: () => ReactNodeViewRenderer(
    ({HTMLAttributes, node}) => (
      <NodeViewWrapper>
        <Span
          {...HTMLAttributes}
          {...node.attrs}
        >
          {node.textContent}
        </Span>
      </NodeViewWrapper>
    ),
  ),
})

/**
 * Can be used in case we don't want to allow multiple paragraphs/headings
 */
const DisableEnterExt = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
    }
  },
})

export const useExtensions = ({
  toggleLinkDialog,
  config,
  placeholder,
}: {
  config: EditorConfig
  placeholder?: string
  toggleLinkDialog: () => void
}) => {
  const {disableEnter, spanAsParagraph} = config

  const LinkExt = useMemo(() => {
    return Link.extend({
      addKeyboardShortcuts() {
        return {
          'Mod-k': () => {
            toggleLinkDialog()
            return true
          },
        }
      },
    }).configure({
      HTMLAttributes: {'data-testid': IS_TEST ? '_a' : null},
    })
  }, [toggleLinkDialog])

  // if we don't use "extend()" and have multiple editors open at the same time,
  //   The same char count will be displayed for all editors. (the char count of the last editor mounted)
  const CharacterCountInstance = useMemo(() => CharacterCount.extend(), [])

  const extensions = [
    Document,
    Text,
    HeadingExt,
    SmallExt,
    Medium,
    FinePrintExt,
    BoldExt,
    ItalicExt,
    OrderedListExt,
    BulletListExt,
    ListItem,
    LinkExt.configure({
      openOnClick: false,
    }),
    NoBreak,
    Strike,
    HardBreak,
    Superscript,
    Subscript,
    History,
    FootnoteExt,
    CharacterCountInstance,
    Placeholder.configure({placeholder}),
  ]

  if (disableEnter) {
    extensions.push(DisableEnterExt)
  }
  if (spanAsParagraph) {
    extensions.push(ParagraphSpanExt)
  } else {
    extensions.push(ParagraphExt)
  }

  return extensions
}
